import React, { useState } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { FaArrowLeft, FaSave, FaExclamationTriangle, FaTrash } from 'react-icons/fa';
import { IDPProvider, useIDP } from '../contexts/IDPContext';
import { BackButton } from '../../../common/styles';
import LoadingSpinner from '../../../common/LoadingSpinner';
import GeneralTab from './GeneralTab';
import ActionsTab from './ActionsTab';
import HistoryTab from './HistoryTab';
import { EmployeeAvatar } from '../../../common/EmployeeAvatar';
import {
  Container,
  Header,
  HeaderLeft,
  Title,
  HeaderButton,
} from '../../styles';
import styled from '@emotion/styled';
import { COLORS } from '../../../../constants/radar';

const TabContainer = styled.div`
  display: flex;
  gap: 1rem;
  padding: 0 16px;
  margin-bottom: 0;
  background-color: ${COLORS.BACKGROUND_PRIMARY};
`;

const TabButton = styled.button`
  padding: 12px 16px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: ${({ 'data-active': active }) => active === 'true' ? COLORS.PRIMARY : COLORS.GRAY};
  border-bottom: 2px solid ${({ 'data-active': active }) => active === 'true' ? COLORS.PRIMARY : 'transparent'};
  transition: all 0.2s ease-in-out;

  &:hover {
    color: ${COLORS.PRIMARY};
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: ${COLORS.BACKGROUND_SECONDARY};
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
`;

const FormContainer = styled.div`
  flex: 1;
  background: white;
  padding: 24px;
  overflow-y: auto;
`;

// Container for modal usage - fills parent height instead of 100vh
const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${COLORS.BACKGROUND_PRIMARY};
  height: 100%;
  overflow: hidden;
`;

export const IDP_TABS = {
  GENERAL: 'GENERAL',
  ACTIONS: 'ACTIONS',
  HISTORY: 'HISTORY',
};

const IDPFormContent = ({ onBack, hideHeader = false, inModal = false, initialTab = 'GENERAL' }) => {
  const {
    goalData,
    isDirty,
    isLoading,
    isSaving,
    readonly,
    saveGoal,
    deleteGoal,
  } = useIDP();

  const [activeTab, setActiveTab] = useState(IDP_TABS[initialTab] || IDP_TABS.GENERAL);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleBack = () => {
    if (isDirty) {
      setShowUnsavedChangesDialog(true);
    } else {
      onBack();
    }
  };

  const handleConfirmNavigation = () => {
    setShowUnsavedChangesDialog(false);
    onBack();
  };

  const handleCancelNavigation = () => {
    setShowUnsavedChangesDialog(false);
  };

  const handleSaveAndContinue = async () => {
    const success = await saveGoal();
    if (success) {
      setShowUnsavedChangesDialog(false);
      onBack();
    }
  };

  const handleSave = async () => {
    await saveGoal();
  };

  const handleDelete = async () => {
    const success = await deleteGoal();
    if (success) {
      setShowDeleteDialog(false);
      onBack();
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading development goal..." />;
  }

  const isSaveButtonDisabled = isSaving || !isDirty || readonly;

  const WrapperComponent = inModal ? ModalContainer : Container;

  return (
    <WrapperComponent>
      {!hideHeader && (
        <Header>
          <HeaderLeft>
            <BackButton onClick={handleBack}>
              <FaArrowLeft /> Back to Development Plans
            </BackButton>
            <Title>{goalData?.title || 'Development Goal'}</Title>
            {isDirty && !readonly && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                ml: 2,
                backgroundColor: 'rgba(255, 193, 7, 0.1)',
                px: 2,
                py: 0.5,
                borderRadius: 1,
                border: '1px solid rgba(255, 193, 7, 0.5)'
              }}>
                <FaExclamationTriangle color="#f59e0b" style={{ marginRight: '8px' }} />
                <Typography variant="body2" color="warning.dark" fontWeight="medium">
                  Unsaved changes
                </Typography>
              </Box>
            )}
          </HeaderLeft>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {!readonly && (
              <>
                <HeaderButton
                  onClick={() => setShowDeleteDialog(true)}
                  style={{
                    borderColor: '#dc2626',
                    color: '#dc2626',
                  }}
                >
                  <FaTrash /> Delete
                </HeaderButton>
                <HeaderButton
                  onClick={handleSave}
                  disabled={isSaveButtonDisabled}
                  style={{
                    ...(isDirty && {
                      backgroundColor: COLORS.PRIMARY,
                      color: 'white',
                    })
                  }}
                >
                  <FaSave /> {isSaving ? 'Saving...' : 'Save'}
                </HeaderButton>
              </>
            )}
          </Box>
        </Header>
      )}

      <Content>
        {/* Employee Section - Above Tabs */}
        {goalData?.employee && (
          <Box
            sx={{
              p: 2,
              mx: 2,
              mt: 1,
              backgroundColor: '#ffffff',
              borderRadius: 1,
              border: '0.5px solid rgba(0, 0, 0, 0.23)',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <EmployeeAvatar employee={goalData.employee} size={48} />
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {goalData.employee.fullName}
              </Typography>
              {(goalData.employee.orgPositions?.label || goalData.employee.orgGrades?.label) && (
                <Typography variant="body2" color="text.secondary">
                  {[goalData.employee.orgPositions?.label, goalData.employee.orgGrades?.label].filter(Boolean).join(' • ')}
                </Typography>
              )}
              {goalData.employee.email && (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  {goalData.employee.email}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        <TabContainer>
          <TabButton
            data-active={(activeTab === IDP_TABS.GENERAL).toString()}
            onClick={() => setActiveTab(IDP_TABS.GENERAL)}
          >
            General
          </TabButton>
          <TabButton
            data-active={(activeTab === IDP_TABS.ACTIONS).toString()}
            onClick={() => setActiveTab(IDP_TABS.ACTIONS)}
          >
            Actions ({goalData?.actions?.length || 0})
          </TabButton>
          <TabButton
            data-active={(activeTab === IDP_TABS.HISTORY).toString()}
            onClick={() => setActiveTab(IDP_TABS.HISTORY)}
          >
            History
          </TabButton>
        </TabContainer>

        <FormContainer>
          {activeTab === IDP_TABS.GENERAL && <GeneralTab />}
          {activeTab === IDP_TABS.ACTIONS && <ActionsTab />}
          {activeTab === IDP_TABS.HISTORY && <HistoryTab />}
        </FormContainer>
      </Content>

      {/* Unsaved Changes Dialog */}
      <Dialog
        open={showUnsavedChangesDialog}
        onClose={handleCancelNavigation}
      >
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes that will be lost if you navigate away from this page.
            Would you like to save your changes before leaving?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmNavigation} color="error">
            Discard Changes
          </Button>
          <Button onClick={handleCancelNavigation} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleSaveAndContinue}
            variant="contained"
            color="primary"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save & Continue'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
      >
        <DialogTitle>Delete Development Goal</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this development goal? This action cannot be undone.
            All associated actions and resources will also be deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </WrapperComponent>
  );
};

const IDPForm = ({ goalId, onBack, readonly = false }) => {
  return (
    <IDPProvider goalId={goalId} readonly={readonly}>
      <IDPFormContent onBack={onBack} />
    </IDPProvider>
  );
};

export { IDPFormContent };
export default IDPForm;
